import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import {
    Brand,
    CloseSessionReason,
    Features,
    GetTokenErrorCodes,
    KickReasons,
} from '../../models/enums/general-enums';
import DialogType from '../ui/enums/DialogType';
import { ILogout, ILogoutData } from './models';
import { FeatureAvailabilityToken, UserContextToken, WindowToken } from '../../injection-tokens';
import {
    IDialogTopicPayload,
    IIdentityChannel,
    ILogger,
    IUiChannel,
    MessageBroker,
    ProductPackageToID,
} from '@sparkware/uc-sdk-core';
import { IUserContext } from '../user-context/user-context-interface';
import { IFeatureAvailability } from '../feature/feature-availability/feature-availability-interface';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import {
    LocalSimpleStoreService,
    SessionSimpleStoreService,
} from '../storage/implementations/simple-store';
import { CookieStoreItemService } from '../storage/implementations';
import LogoutApi from '../../../APIs/LogoutApi';
import TokenApi from '../../../APIs/TokenApi';

@Service()
export class Logout implements ILogout {
    private readonly _window: Window;
    private readonly _uiChannel: IUiChannel;
    private readonly _userContext: IUserContext;
    private readonly _featureAvailability: IFeatureAvailability;
    private readonly _sessionStorage: SessionSimpleStoreService;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _cookieStoreItemService: CookieStoreItemService;
    private readonly _logger: ILogger;
    private readonly _identityChannel: IIdentityChannel;

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('Logout');
        this._window = Container.get(WindowToken);
        this._userContext = Container.get(UserContextToken);
        this._featureAvailability = Container.get(FeatureAvailabilityToken);
        this._sessionStorage = Container.get(SessionSimpleStoreService);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._cookieStoreItemService = Container.get(CookieStoreItemService);

        this._uiChannel = MessageBroker.getInstance().ui;
        this._identityChannel = MessageBroker.getInstance().identity;
    }

    public async logoutFinishedCallback(result: any, kickReasonId?: number): Promise<void> {
        const userId = this._window.pageContextManager.getUserData()?.cid || 0;
        const globalSessionId = this._window.pageContextManager.getSessionData()?.globalSessionId;
        if (!globalSessionId) {
            this._logger.debug('Logout was already performed, no need to do it again...');
            return;
        }

        if (result?.HasOtherLoginSession) {
            this._window.location.reload();
            return;
        }

        this.cleanUserStorageItems();
        this.removeLobbyCookie();

        if (result) {
            const reason = kickReasonId || CloseSessionReason.ClientDisconnected;
            const logoutData: any = {
                InitialState: this._window.__UAA_STATE__,
                CID: userId,
                Reason: reason,
            };
            this._identityChannel.topics.preLogoutSuccess.publish(
                {
                    publisher: 'logout',
                },
                logoutData,
            );
        }
    }

    public async doLogout(reason?: number): Promise<void> {
        if (this._userContext.IsAuthenticated) {
            const data: ILogoutData = { IsManuelLogout: true };

            const result = await LogoutApi.Logout(data);
            await this.logoutFinishedCallback(result, reason);
        }
    }

    public async doLogoutWithoutRefresh(
        kickReasonId?: number,
        isLogoutFromPlatform?: boolean,
    ): Promise<boolean> {
        if (!this._userContext.IsAuthenticated) {
            return false;
        }

        const clientInactivityKickReasonId = 23;
        if (kickReasonId === clientInactivityKickReasonId) {
            await this.doLogoutRefreshState(null, kickReasonId);
            return true;
        }

        const data: ILogoutData = {
            KickReasonId: kickReasonId,
            IsLogoutFromPlatform: isLogoutFromPlatform ?? true,
        };

        const result = await LogoutApi.Logout(data);
        await this.logoutFinishedCallback(result, kickReasonId);
    }

    public async doLogoutAndShowTimeoutDialog(
        kickReasonId?: number,
        timeoutKickOptions?: any,
    ): Promise<void> {
        await this.doLogoutWithoutRefresh(kickReasonId);
        if (kickReasonId !== undefined && kickReasonId !== null) {
            this._sessionStorage.set(StorageItemEnum.LastKickReasonId, kickReasonId.toString());
        }

        const isSingleTabLoggedInEnabled = this._featureAvailability.IsFeatureEnabled(
            Features.SINGLE_TAB_LOGGED_IN,
        );
        if (kickReasonId === KickReasons.UserAlreadyLoggedIn && isSingleTabLoggedInEnabled) {
            timeoutKickOptions.contentProperty = 'duplicateLogins';
        }

        if (
            this._window.location.pathname !== '/play/' &&
            kickReasonId !== undefined &&
            kickReasonId !== KickReasons.NoWifiOrInactivity
        ) {
            this.showDialog(DialogType.TimeoutAndKick, timeoutKickOptions);
        }
    }

    public async doLogoutRefreshState(logoutData: any, kickReasonId?: number): Promise<void> {
        const data = {
            InitialState: this._window.__UAA_STATE__,
        };

        const lastKickReasonId =
            kickReasonId || this._sessionStorage.get(StorageItemEnum.LastKickReasonId);
        const isUserOnBackToForeground =
            this._localSimpleStoreService.get(StorageItemEnum.UserOnBackToForeground) === 'true';

        const timeLimitReasons = [30, 39, 40];
        const brandId = this._window.pageContextManager.getBrandData().brandId;
        if (
            lastKickReasonId != KickReasons.NoWifiOrInactivity &&
            logoutData?.isDialogShown &&
            (!isUserOnBackToForeground || timeLimitReasons.includes(kickReasonId!))
        ) {
            const dialogType = logoutData.dialog?.type || DialogType.TimeoutAndKick;
            const dialogOptions = logoutData.dialog?.options || {};

            this.showDialog(dialogType, dialogOptions);
        } else if (
            brandId === Brand.Sport_DE &&
            lastKickReasonId == KickReasons.NoWifiOrInactivity
        ) {
            //DE Regulation condition.
            this.showDialog(DialogType.TimeoutAndKick, {});
        }

        this.cleanUserStorageItems();
        await this.logoutFinishedCallback(data, kickReasonId);
    }

    public doSoftKick(reasonCode: number, correlationId?: string): void {
        const isDialogShown = true;
        const dialogOptions = {
            content: 'content',
            reason: reasonCode,
            correlationId: correlationId,
        };

        this._window.document
            .querySelectorAll('.sportErrorContent')
            .forEach((el) => ((el as HTMLElement).style.display = 'none'));

        let dialogType = DialogType.TimeoutAndKick;

        switch (reasonCode) {
            case KickReasons.TimeLimitReachedError_Daily:
                dialogOptions.content = 'timeLimitDaily';
                break;
            case KickReasons.TimeLimitReachedError_Weekly:
                dialogOptions.content = 'timeLimitWeekly';
                break;
            case KickReasons.TimeLimitReachedError_Monthly:
                dialogOptions.content = 'timeLimitMonthly';
                break;
            case KickReasons.UKNightLimitReached:
                const autoLoginSourceProductPackage =
                    this._window.NightlyLimitAutoLoginSourceProductPackage;
                const pokerNativeProductPackageIds = [
                    ProductPackageToID.POKER_NATIVE_ANDROID_IS,
                    ProductPackageToID.POKER_NATIVE_ANDROID_OOS,
                    ProductPackageToID.POKER_NATIVE_iOS,
                ];

                const isNativePokerSourceAutoLogin =
                    autoLoginSourceProductPackage &&
                    pokerNativeProductPackageIds.some(
                        (x) => x.toString() === autoLoginSourceProductPackage,
                    );

                if (!isNativePokerSourceAutoLogin) {
                    dialogType = DialogType.NightlyLimitRestriction;
                    this.showDialog(dialogType, dialogOptions);
                }
                return;
            default:
                const defaultErrorElement = this._window.document.getElementById('defaultError');
                if (defaultErrorElement) {
                    (defaultErrorElement as HTMLElement).style.display = 'block';
                }
                break;
        }

        const logoutData = {
            isDialogShown,
            dialog: {
                type: dialogType,
                options: dialogOptions,
            },
        };

        this.doLogoutRefreshState(logoutData, reasonCode);
    }

    public cleanUserStorageItems(): void {
        const items = this._window.LocalStorageRemovableUserItems;
        if (items) {
            items.forEach((item: string) => {
                this._sessionStorage.remove(item);
                this._localSimpleStoreService.remove(item);
            });
        }
    }

    public removeLobbyCookie(): void {
        this._cookieStoreItemService.remove('lobbyVisited');
    }

    public async onGetToken(): Promise<void> {
        const getTokenResponse = await TokenApi.GetToken();
        if (getTokenResponse?.ErrorCode == GetTokenErrorCodes.NoRecordsFound) {
            await this.doLogoutAndShowTimeoutDialog();
        }
        const token = getTokenResponse && getTokenResponse.Token;
        this._identityChannel.topics.initThirdParty.publish(
            { publisher: 'logout' },
            { Token: token },
        );
    }

    private showDialog(dialogType: string, options?: any): void {
        this._uiChannel.topics.Dialog.publish(
            {
                publisher: 'logout',
            },
            {
                dialogType: dialogType,
                options: options,
            } as IDialogTopicPayload,
        );
    }
}
